// src/components/ManageJobBoard.tsx
import React, { useState, useEffect, useCallback } from "react";
import { 
  Box, Typography, Stack, Alert, Snackbar, 
  useTheme, Dialog, DialogTitle, DialogContent, 
  DialogContentText, DialogActions, Button, LinearProgress,
  Chip, Tooltip, Fade, IconButton
} from "@mui/material";
import { 
  DndContext, DragEndEvent, DragStartEvent, 
  DragOverlay, closestCorners, KeyboardSensor, 
  PointerSensor, useSensor, useSensors 
} from "@dnd-kit/core";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { KanbanColumn, KanbanCard } from "./KanbanComponents";
import { Job, JobsState, ColumnId } from "../types/job";
import { fetchJobs, updateJobStatus } from "../Services/jobService";
import JobDetailSection from "./JobDetailSection";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const ManageJobBoard: React.FC = () => {
  const theme = useTheme();
  
  // State for jobs data
  const [jobs, setJobs] = useState<JobsState>({
    open: [],
    inReview: [],
    closed: []
  });
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  // Active job for drag operations
  const [activeJob, setActiveJob] = useState<Job | null>(null);
  
  // Dialog for confirmation
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean,
    title: string,
    message: string,
    onConfirm: () => void
  }>({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {}
  });
  
  // Notification state
  const [notification, setNotification] = useState<{
    open: boolean, 
    message: string, 
    severity: 'success' | 'warning' | 'error' | 'info'
  }>({
    open: false,
    message: "",
    severity: "success"
  });

  // Job details panel state
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);

  // Fetch jobs data on component mount
  useEffect(() => {
    const loadJobs = async () => {
      try {
        setIsLoading(true);
        // Simulate progress during loading for better UX
        const interval = setInterval(() => {
          setLoadingProgress(prev => {
            const nextProgress = prev + Math.random() * 15;
            return nextProgress >= 100 ? 100 : nextProgress;
          });
        }, 300);

        const jobsData = await fetchJobs();
        setJobs(jobsData);
        setError(null);
        clearInterval(interval);
        setLoadingProgress(100);
        
        // Small delay to show 100% completion before hiding
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      } catch (err) {
        setError("Failed to load jobs. Please try again later.");
        console.error("Error loading jobs:", err);
        setIsLoading(false);
      }
    };

    loadJobs();
  }, []);

  // Using sensors for better accessibility
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Minimum drag distance
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const activeColumn = active.data.current?.sortable.containerId as ColumnId;
    
    const jobIndex = jobs[activeColumn].findIndex(job => job.id === active.id);
    if (jobIndex !== -1) {
      setActiveJob(jobs[activeColumn][jobIndex]);
    }
    
    // Close details panel when dragging starts
    setDetailsPanelOpen(false);
  };

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveJob(null);
    
    if (!over) return;
    
    const activeColumn = active.data.current?.sortable.containerId as ColumnId;
    const overColumn = over.id as ColumnId;
    
    // If same column, just reorder
    if (activeColumn === overColumn) {
      const activeIndex = jobs[activeColumn].findIndex(job => job.id === active.id);
      const overIndex = jobs[overColumn].findIndex(job => job.id === over.id);
      
      if (activeIndex !== overIndex) {
        setJobs(prev => ({
          ...prev,
          [activeColumn]: arrayMove(prev[activeColumn], activeIndex, overIndex)
        }));
      }
      return;
    }
    
    // Prevent moving closed jobs
    if (activeColumn === "closed") {
      setNotification({
        open: true,
        message: "Closed jobs cannot be modified",
        severity: "error"
      });
      return;
    }
    
    // For other column transitions, show confirmation dialog
    const activeJobData = jobs[activeColumn].find(job => job.id === active.id);
    if (!activeJobData) return;
    
    if (overColumn === "inReview" && activeColumn === "open") {
      // Check if deadline exists before trying to parse it
      if (!activeJobData.deadline) {
        moveJobBetweenColumns(activeJobData, activeColumn, overColumn);
        return;
      }
      
      const deadlineDate = new Date(activeJobData.deadline);
      const today = new Date();
      const formattedDeadline = new Intl.DateTimeFormat('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric' 
      }).format(deadlineDate);
      
      const isDeadlinePassed = deadlineDate < today;
      
      setConfirmDialog({
        open: true,
        title: "Move to Review",
        message: isDeadlinePassed 
          ? `The deadline for "${activeJobData.title}" (${formattedDeadline}) has passed. Do you want to move this job to review?`
          : `The deadline for "${activeJobData.title}" is set to ${formattedDeadline}. Do you want to move this job to review?`,
        onConfirm: () => moveJobBetweenColumns(activeJobData, activeColumn, overColumn)
      });
    } else if (overColumn === "closed" && activeColumn === "inReview") {
      setConfirmDialog({
        open: true,
        title: "Close Job",
        message: `Are you sure you want to close "${activeJobData.title}"? This will finalize the hiring process.`,
        onConfirm: () => moveJobBetweenColumns(activeJobData, activeColumn, overColumn)
      });
    } else if (overColumn === "open" && activeColumn === "inReview") {
      setConfirmDialog({
        open: true,
        title: "Reopen Job",
        message: `Are you sure you want to reopen "${activeJobData.title}"? This will reset the review process.`,
        onConfirm: () => moveJobBetweenColumns(activeJobData, activeColumn, overColumn)
      });
    } else {
      // For any other transitions
      moveJobBetweenColumns(activeJobData, activeColumn, overColumn);
    }
  }, [jobs]);

  const moveJobBetweenColumns = async (job: Job, fromColumn: ColumnId, toColumn: ColumnId) => {
    // Create a copy of jobs state
    const newJobs = { ...jobs };
    const jobIndex = newJobs[fromColumn].findIndex(j => j.id === job.id);
    
    if (jobIndex === -1) return;
    
    // Remove job from source column
    const [movedJob] = newJobs[fromColumn].splice(jobIndex, 1);
    
    // Update job properties based on destination column
    if (toColumn === "inReview") {
      movedJob.reviewStarted = new Date().toLocaleString();
      movedJob.shortlistedCount = Math.ceil(movedJob.applicationsCount * 0.3);
      delete movedJob.deadline;
    } else if (toColumn === "open") {
      // Set a new deadline one week from now
      movedJob.deadline = new Date(
        Date.now() + 7 * 24 * 60 * 60 * 1000
      ).toISOString().split("T")[0];
      delete movedJob.reviewStarted;
      delete movedJob.shortlistedCount;
      delete movedJob.closedDate;
      delete movedJob.finalizedCount;
    } else if (toColumn === "closed") {
      movedJob.closedDate = new Date().toLocaleString();
      movedJob.finalizedCount = Math.ceil(movedJob.shortlistedCount! * 0.5);
    }
    
    // Add to target column
    newJobs[toColumn].unshift(movedJob);
    
    try {
      // Optimistic UI update
      setJobs(newJobs);
      
      // Update job status in the backend
      await updateJobStatus(job.id, toColumn);
      
      // Show success notification
      setNotification({
        open: true,
        message: getSuccessMessage(job.title, toColumn),
        severity: "success"
      });
      
      // If the job was selected, update the selected job with new data
      if (selectedJob && selectedJob.id === job.id) {
        setSelectedJob(movedJob);
      }
    } catch (error) {
      console.error("Failed to update job status:", error);
      
      // Revert changes on error
      setJobs(jobs);
      
      setNotification({
        open: true,
        message: "Failed to update job status. Please try again.",
        severity: "error"
      });
    }
  };

  const handleCardClick = (job: Job) => {
    setSelectedJob(job);
    setDetailsPanelOpen(true);
  };

  const getSuccessMessage = (jobTitle: string, column: ColumnId): string => {
    switch (column) {
      case "open":
        return `"${jobTitle}" reopened`;
      case "inReview":
        return `"${jobTitle}" moved to review`;
      case "closed":
        return `"${jobTitle}" closed`;
      default:
        return `"${jobTitle}" updated`;
    }
  };

  const closeNotification = () => {
    setNotification(prev => ({...prev, open: false}));
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({...prev, open: false}));
  };

  const getColumnTitle = (columnId: ColumnId): string => {
    switch (columnId) {
      case "open": return "Open";
      case "inReview": return "In Review";
      case "closed": return "Closed";
      default: return "";
    }
  };

  const getTotalJobsCount = () => {
    return jobs.open.length + jobs.inReview.length + jobs.closed.length;
  };

  const handleCloseDetailsPanel = () => {
    setDetailsPanelOpen(false);
    setTimeout(() => {
      setSelectedJob(null);
    }, 300); // Delay to allow animation to complete
  };

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: "#f5f7fa" 
      }}>
        <Typography variant="h6" sx={{ mb: 3, color: theme.palette.primary.main }}>
          Loading Job Board
        </Typography>
        <Box sx={{ width: '80%', maxWidth: 400, mb: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={loadingProgress} 
            sx={{ 
              height: 6, 
              borderRadius: 3,
              backgroundColor: theme.palette.grey[200]
            }}
          />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: "#f5f7fa"
      }}>
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            width: '100%', 
            maxWidth: 400
          }}
        >
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ borderRadius: 2 }}
        >
          Reload
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: "#f5f7fa", 
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }}>
      {/* Compact Header */}
      {/* <Box 
        sx={{ 
          backgroundColor: "#ffffff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          p: 1.5,
          position: "sticky",
          top: 0,
          zIndex: 10,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            Job Board
          </Typography>
          <Chip 
            label={`${getTotalJobsCount()} jobs`} 
            size="small"
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 1 }}
          />
        </Stack>
        
        <Button 
          variant="contained" 
          size="small"
          startIcon={<AddIcon />}
          sx={{ borderRadius: 1.5 }}
        >
          New Job
        </Button>
      </Box> */}

      {/* Help tip */}
      <Box sx={{ 
        backgroundColor: "rgba(25, 118, 210, 0.05)", 
        py: 0.75, 
        px: 2, 
        display: "flex", 
        alignItems: "center",
        justifyContent: "center"
      }}>
        <InfoOutlinedIcon fontSize="small" sx={{ mr: 1, color: "primary.main", fontSize: "0.9rem" }} />
        <Typography variant="caption" color="primary.main">
          Drag jobs between columns to change status
        </Typography>
      </Box>

      {/* Board content with centered columns */}
      <Box sx={{ 
        flex: 1,
        position: "relative",
        height: "calc(100vh - 84px)",
        display: "flex",
        overflowY: "hidden"
      }}>
        {/* Main board with columns */}
        <Box 
          sx={{ 
            flex: detailsPanelOpen ? "1 0 60%" : "1 0 100%",
            transition: "flex 0.3s ease",
            p: 2,
            pb: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                maxWidth: "1200px",
                width: "100%",
                height: "calc(100vh - 100px)",
                overflowX: "auto",
                pb: 2,
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0,0,0,0.03)",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.15)",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.2)",
                  }
                }
              }}
              role="region"
              aria-label="Job kanban board"
            >
              {(["open", "inReview", "closed"] as const).map((columnId) => (
                <SortableContext key={columnId} items={jobs[columnId].map(job => job.id)}>
                  <KanbanColumn 
                    id={columnId} 
                    title={getColumnTitle(columnId)} 
                    jobs={jobs[columnId]}
                   // onCardClick={handleCardClick}
                  />
                </SortableContext>
              ))}
              
              <DragOverlay>
                {activeJob && (
                  <KanbanCard 
                    id={activeJob.id}
                    title={activeJob.title}
                    applicationsCount={activeJob.applicationsCount}
                    deadline={activeJob.deadline}
                    reviewStarted={activeJob.reviewStarted}
                    closedDate={activeJob.closedDate}
                    columnId=""
                    shortlistedCount={activeJob.shortlistedCount}
                    finalizedCount={activeJob.finalizedCount}
                  />
                )}
              </DragOverlay>
            </Box>
          </DndContext>
        </Box>

        {/* Details panel (slide in from right) */}
        <Fade in={detailsPanelOpen}>
          <Box
            sx={{
              position: { xs: "fixed", md: "relative" },
              right: 0,
              top: { xs: 0, md: 0 },
              height: "100%",
              width: { xs: "100%", sm: "400px", md: "40%" },
              maxWidth: "450px",
              backgroundColor: "#ffffff",
              boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
              zIndex: { xs: 1200, md: 5 },
              overflowY: "auto",
              transform: detailsPanelOpen ? "translateX(0)" : "translateX(100%)",
              transition: "transform 0.3s ease-in-out"
            }}
          >
            <Box sx={{ 
              p: 1.5, 
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <Typography variant="subtitle1" fontWeight="medium">
                Job Details
              </Typography>
              <IconButton 
                size="small"
                onClick={handleCloseDetailsPanel}
                sx={{ p: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Box sx={{ p: 2 }}>
              {selectedJob && <JobDetailSection job={selectedJob} />}
            </Box>
          </Box>
        </Fade>
      </Box>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[10],
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={closeConfirmDialog} 
            color="inherit"
            variant="outlined"
            size="small"
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              confirmDialog.onConfirm();
              closeConfirmDialog();
            }} 
            color="primary" 
            variant="contained"
            size="small"
            sx={{ borderRadius: 1.5, ml: 1 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notifications */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={3000} 
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert 
          onClose={closeNotification} 
          severity={notification.severity} 
          sx={{ width: '100%', borderRadius: 1.5 }}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageJobBoard;